import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { SSO_LOGIN_ENABLED } from "@/lib/sso";
import { showSelfRegistrationDisabledToast } from "@/lib/registration";



export function LoginMfaForm({
  mfaCode,
  mfaError,
  loading,
  onCodeChange,
  onSubmit,
  onBack,
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4 min-w-0" data-testid="mfa-form">
      <div className="space-y-1.5">
        <div className="text-[13px] font-semibold text-slate-900">Two-factor authentication</div>
        <div className="text-[12px] text-slate-600">Enter the 6-digit code from your authenticator app.</div>
      </div>
      <div className="space-y-1.5">
        <label htmlFor="mfa-code" className="text-[12px] font-medium text-slate-700">Authentication code</label>
        <input
          id="mfa-code"
          data-testid="mfa-code-input"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          autoComplete="one-time-code"
          value={mfaCode}
          onChange={onCodeChange}
          className="w-full min-h-11 rounded-md border border-slate-300 px-3 py-2.5 text-center tracking-[0.35em] sm:tracking-[0.4em] font-mono text-lg focus:border-indigo-500 focus:outline-none"
        />
      </div>
      {mfaError ? (
        <div data-testid="mfa-form-error" role="alert" className="text-[12px] text-rose-600">
          {mfaError}
        </div>
      ) : null}
      <button
        type="submit"
        disabled={loading}
        data-testid="mfa-verify-btn"
        className="inline-flex w-full min-h-11 items-center justify-center rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {loading ? "Verifying…" : "Verify"}
      </button>
      <button
        type="button"
        data-testid="mfa-back-btn"
        onClick={onBack}
        className="min-h-10 text-xs text-slate-600 hover:text-slate-900"
      >
        Use a different account
      </button>
    </form>
  );
}

export function LoginPendingDeletionBanner({ pendingDel, cancelBusy, onCancel, onDismiss }) {
  return (
    <div
      className="rounded-lg bg-rose-50 ring-1 ring-rose-200 px-3 py-3 text-[12.5px] text-rose-900"
      data-testid="login-pending-deletion-banner"
    >
      <div className="font-semibold mb-1">Account scheduled for deletion</div>
      <div className="text-rose-700 mb-2">
        {pendingDel.scheduled_at
          ? `Permanent purge on ${new Date(pendingDel.scheduled_at).toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" })}.`
          : "Deletion is in progress."}{" "}
        You can cancel now and keep your data.
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Button
          type="button"
          size="sm"
          className="h-10 sm:h-8 w-full sm:w-auto bg-rose-600 hover:bg-rose-700"
          onClick={onCancel}
          disabled={cancelBusy}
          data-testid="login-cancel-deletion"
        >
          {cancelBusy ? "Restoring…" : "Cancel deletion & sign in"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-10 sm:h-8 w-full sm:w-auto"
          onClick={onDismiss}
          data-testid="login-pending-dismiss"
        >
          Use a different account
        </Button>
      </div>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.56c2.08-1.92 3.28-4.74 3.28-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.56-2.77c-.99.66-2.25 1.06-3.72 1.06-2.87 0-5.3-1.94-6.16-4.54H2.18v2.85A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.47 12c0-.73.13-1.44.37-2.1V7.05H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.95l3.66-2.85z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.65l3.16-3.16C17.46 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.85C6.7 7.32 9.13 5.38 12 5.38z"/>
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
      <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
      <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
    </svg>
  );
}

export function LoginCredentialsForm({
  pendingDel,
  cancelBusy,
  centralLogin,
  workspaceUrl,
  workspaceUrlError,
  email,
  emailError,
  password,
  loading,
  ssoLoading,
  onWorkspaceUrlChange,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onCancelDeletion,
  onDismissPendingDeletion,
  onSso,
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4 min-w-0 w-full" noValidate data-testid="login-form">
      {pendingDel ? (
        <LoginPendingDeletionBanner
          pendingDel={pendingDel}
          cancelBusy={cancelBusy}
          onCancel={onCancelDeletion}
          onDismiss={onDismissPendingDeletion}
        />
      ) : null}
      {SSO_LOGIN_ENABLED ? (
        <>
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <Button type="button" variant="outline" className="min-h-11 sm:min-h-10" onClick={() => onSso("google")} disabled={!!ssoLoading} data-testid="login-google">
              {ssoLoading === "google" ? <Loader2 size={14} className="animate-spin" /> : <GoogleMark />}
              Google
            </Button>
            <Button type="button" variant="outline" className="min-h-11 sm:min-h-10" onClick={() => onSso("microsoft")} disabled={!!ssoLoading} data-testid="login-microsoft">
              {ssoLoading === "microsoft" ? <Loader2 size={14} className="animate-spin" /> : <MicrosoftMark />}
              Microsoft
            </Button>
          </div>
          <div className="relative">
            <div className="h-px bg-slate-200" />
            <span className="absolute inset-0 -top-2 flex justify-center">
              <span className="bg-white px-2 text-[11px] text-slate-400 uppercase tracking-wider">or</span>
            </span>
          </div>
        </>
      ) : null}
      {centralLogin ? (
        <div className="space-y-1.5 min-w-0">
          <Label htmlFor="workspace-url" className="text-[12px]">Workspace URL</Label>
          <Input
            id="workspace-url"
            type="text"
            autoComplete="url"
            inputMode="url"
            value={workspaceUrl}
            onChange={onWorkspaceUrlChange}
            placeholder="your-company.sellix.app"
            className="h-11 sm:h-10 w-full min-w-0"
            data-testid="login-workspace-url"
          />
          {workspaceUrlError ? (
            <p className="text-[11.5px] text-rose-600 break-words" role="alert" data-testid="login-workspace-url-error">
              {workspaceUrlError}
            </p>
          ) : (
            <p className="text-[11px] text-slate-500">Include the full domain (e.g. .app, .com, or .io).</p>
          )}
        </div>
      ) : null}
      <div className="space-y-1.5 min-w-0">
        <Label htmlFor="email" className="text-[12px]">Work email</Label>
        <Input
          id="email"
          type="text"
          inputMode="email"
          autoComplete="username"
          spellCheck={false}
          value={email}
          onChange={onEmailChange}
          placeholder="you@company.com"
          className={emailError ? "h-11 sm:h-10 w-full min-w-0 border-rose-400 focus-visible:ring-rose-400" : "h-11 sm:h-10 w-full min-w-0"}
          aria-invalid={emailError ? "true" : "false"}
          aria-describedby={emailError ? "login-email-error" : undefined}
          data-testid="login-email"
        />
        {emailError ? (
          <p
            id="login-email-error"
            className="text-[11.5px] text-rose-600 break-words"
            role="alert"
            data-testid="login-email-error"
          >
            {emailError}
          </p>
        ) : (
          <p className="text-[11px] text-slate-500">
            Include a full domain (for example: you@company.com).
          </p>
        )}
      </div>
      <div className="space-y-1.5 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="password" className="text-[12px] shrink-0">Password</Label>
          <Link to="/forgot-password" className="text-[12px] text-indigo-600 hover:underline py-1 shrink-0" data-testid="forgot-link">
            Forgot password?
          </Link>
        </div>
        <PasswordInput
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={onPasswordChange}
          placeholder="Enter your password"
          className="h-11 sm:h-10 w-full min-w-0 placeholder:text-slate-400"
          data-testid="login-password"
          toggleTestId="login-password-toggle"
        />
      </div>
      <Button type="submit" className="w-full h-11 sm:h-10" disabled={loading} data-testid="login-submit">
        {loading ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
        Sign in with email
      </Button>
      <p className="text-[12px] text-slate-500 text-center">
        New to Sellyx?{" "}
        <button
          type="button"
          onClick={showSelfRegistrationDisabledToast}
          className="text-indigo-600 hover:underline font-medium min-h-10 inline-flex items-center"
          data-testid="login-register-link"
        >
          Create an account
        </button>
      </p>
    </form>
  );
}

LoginMfaForm.propTypes = {
  mfaCode: PropTypes.any,
  mfaError: PropTypes.any,
  loading: PropTypes.any,
  onCodeChange: PropTypes.any,
  onSubmit: PropTypes.any,
  onBack: PropTypes.any,
};

LoginPendingDeletionBanner.propTypes = {
  pendingDel: PropTypes.any,
  cancelBusy: PropTypes.any,
  onCancel: PropTypes.any,
  onDismiss: PropTypes.any,
};

LoginCredentialsForm.propTypes = {
  pendingDel: PropTypes.any,
  cancelBusy: PropTypes.any,
  centralLogin: PropTypes.any,
  workspaceUrl: PropTypes.any,
  workspaceUrlError: PropTypes.any,
  email: PropTypes.any,
  emailError: PropTypes.any,
  password: PropTypes.any,
  loading: PropTypes.any,
  ssoLoading: PropTypes.any,
  onWorkspaceUrlChange: PropTypes.any,
  onEmailChange: PropTypes.any,
  onPasswordChange: PropTypes.any,
  onSubmit: PropTypes.any,
  onCancelDeletion: PropTypes.any,
  onDismissPendingDeletion: PropTypes.any,
  onSso: PropTypes.any,
};
